/**
 * PATH: packages/email-hub/src/feature/components/WhoCanSeeFlow.tsx
 * FILE_ID: EMAIL-WHOCANSEE-FLOW-001
 * PURPOSE: Pure presentational "who can see this email" provenance flow chart —
 *          a spine from the email branching to each access channel (owner /
 *          shared mailbox / board / explicit / org admins), each listing the
 *          people it grants visibility to with their org role/position.
 * WHY:     The org has no reporting hierarchy to draw, so the useful "flow chart
 *          in the org structure" is the visibility PROVENANCE: who can see it AND
 *          why. Data comes from GET /api/emails/<id>/who-can-see/.
 * FLOW:    WhoCanSeeData → spine + per-channel branches → member nodes.
 * RELATED:
 *   - WhoCanSeeButton.tsx: fetches the data + hosts this in a popover.
 *   - organisation/services/email_who_can_see.py: the backend resolver.
 */

import { Eye, KeyRound, Mail, Shield, SquareKanban, User, Users } from 'lucide-react'
import { cn } from '../../_internal/lib/utils'
import {
  avatarTint,
  memberInitials,
  type WhoCanSeeData,
  type WhoCanSeeMember,
} from './whoCanSee'

const CHANNEL_ICON: Record<string, typeof User> = {
  OWNER: User,
  SHARED_MAILBOX: Users,
  BOARD: SquareKanban,
  EXPLICIT: KeyRound,
  ORG_ADMIN: Shield,
}

function MemberNode({ member }: { member: WhoCanSeeMember }) {
  const subtitle = member.position || member.role || member.email
  return (
    <div
      className={cn(
        'flex items-center gap-1.5 rounded-md border border-border bg-background px-1.5 py-1',
        member.is_primary && 'ring-1 ring-blue-400/60',
      )}
      data-testid={`who-can-see-member-${member.id}`}
    >
      <span
        className={cn(
          'flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[9px] font-semibold',
          avatarTint(member.email || String(member.id)),
        )}
      >
        {memberInitials(member.name)}
      </span>
      <span className="min-w-0">
        <span className="block max-w-[120px] truncate text-[11px] leading-tight text-foreground">
          {member.name}
        </span>
        {subtitle && (
          <span className="block max-w-[120px] truncate text-[9px] capitalize leading-tight text-muted-foreground">
            {subtitle}
          </span>
        )}
      </span>
    </div>
  )
}

export function WhoCanSeeFlow({ data }: { data: WhoCanSeeData }) {
  return (
    <div data-testid="who-can-see-flow">
      {/* Root: the email + its primary viewer. */}
      <div className="flex items-center gap-2 rounded-md bg-muted/60 px-2 py-1.5">
        <Mail className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
        <span className="min-w-0 flex-1">
          <span className="block text-[11px] font-semibold text-foreground">This email</span>
          <span className="block truncate text-[10px] text-muted-foreground">
            {data.primary
              ? `Primary: ${data.primary.name}`
              : 'No primary mailbox owner'}
            {' · '}
            {data.total} {data.total === 1 ? 'person' : 'people'} can see it
          </span>
        </span>
      </div>

      {/* Spine + per-channel branches. */}
      <div className="relative mt-1 pl-4">
        <div className="absolute left-1.5 top-0 bottom-2 w-px bg-border" aria-hidden />
        {data.channels.map((ch, i) => {
          const Icon = CHANNEL_ICON[ch.type] ?? Eye
          return (
            <div key={`${ch.type}-${ch.board_id ?? i}`} className="relative pt-2">
              <span
                className="absolute -left-[10px] top-3 h-1.5 w-1.5 rounded-full bg-border"
                aria-hidden
              />
              <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                <Icon className="h-3 w-3" aria-hidden />
                <span className="truncate">{ch.label}</span>
                <span className="text-muted-foreground/60">· {ch.members.length}</span>
              </div>
              <div className="mt-1 flex flex-wrap gap-1">
                {ch.members.map((m) => (
                  <MemberNode key={`${ch.type}-${m.id}`} member={m} />
                ))}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default WhoCanSeeFlow
